import PropTypes from 'prop-types';
import { action, extendObservable } from 'mobx';
import moment from 'moment';
import { apiCall } from '../../../API';
import { lenAwareDomainValidator, lenAwareEmailValidator } from '../../../email';
import { getGroupOptionDetails } from '../../helpers/userGroups';
import AsyncActionStore from '../../AsyncActionStore';
import FlagStore from '../../FlagStore';
import FuseSearchableSelectStore from '../../FuseSearchableSelectStore';
import InputStore from '../../InputStore';
import RadioButtonStore from '../../RadioButtonStore';
import UrlBulkEditorCardStore from '../../UrlBulkEditorCardStore';
import { PolicyModalSectionProps } from '../PolicyModalStore';
import FuseSelectStore from '../../FuseSelectStore';

export const SENDER_TABS = {
    DOMAINS: 'domains',
    EMAILS: 'emails',
};

const DEFAULT_VALUES = {
    matchAllUserGroups: false,
    matchAllSenderEmail: true, // This line makes means NULL in the db is equivalent to True in the frontend :|
    tapUsersGroups: [],
    senderEmailAddresses: [],
    senderEmailDomains: [],
};

export default class RedirectMatchCriteriaStore {
    static PropType = PropTypes.shape({
        originalValue: PropTypes.any,
        groupsLoadStatus: AsyncActionStore.PropType,
        loadLastSyncStatus: AsyncActionStore.PropType,
        lastSyncGroup: PropTypes.number,
        loadLastSyncGroup: PropTypes.func,

        ...PolicyModalSectionProps,

        matchAllUserGroups: FlagStore.PropType,
        matchAllSenderEmail: FlagStore.PropType,
        tapUsersGroups: FuseSelectStore.PropType,
        activeTab: RadioButtonStore.PropType,
        senderEmailAddresses: UrlBulkEditorCardStore.PropType,
        senderEmailDomains: UrlBulkEditorCardStore.PropType,
        editConfirmSenderEmailDomainsAddedSearch: InputStore.PropType,
        editConfirmSenderEmailDomainsRemovedSearch: InputStore.PropType,
        editConfirmSenderEmailAddressesAddedSearch: InputStore.PropType,
        editConfirmSenderEmailAddressesRemovedSearch: InputStore.PropType,
        allGroups: PropTypes.object,
        referencesUnSyncedGroups: PropTypes.func,
    });

    constructor(data = {}) {
        extendObservable(this, /** @class RedirectMatchCriteriaStore */{
            originalValue: {},
            allGroups: {},
            //
            matchAllUserGroups: new FlagStore(),
            matchAllSenderEmail: new FlagStore(),
            apiGroupsList: apiCall('/console/usergroups/list?source=USER_CENTER'),
            loadGroupsStatus: new AsyncActionStore(),
            tapUsersGroups: new FuseSearchableSelectStore({
                sorterFactory: this.groupsSorterFactory,
                getSearchParams: (email) => ({ email, source: 'USER_CENTER' }),
                apiMethod: apiCall('/console/usergroups/search'),
                searchValidator: lenAwareEmailValidator
            }),
            activeTab: new RadioButtonStore(SENDER_TABS.DOMAINS),
            senderEmailAddresses: new UrlBulkEditorCardStore({
                validator: lenAwareEmailValidator
            }),
            senderEmailDomains: new UrlBulkEditorCardStore({
                validator: lenAwareDomainValidator
            }),
            loadLastSyncStatus: new AsyncActionStore(),
            apiLoadLastSync: apiCall('/console/usergroups/cloudsync/updateTime?alwaysDisplay=true'),
            lastSyncGroupDate: null,
            editConfirmSenderEmailDomainsAddedSearch: new InputStore(),
            editConfirmSenderEmailDomainsRemovedSearch: new InputStore(),
            editConfirmSenderEmailAddressesAddedSearch: new InputStore(),
            editConfirmSenderEmailAddressesRemovedSearch: new InputStore(),
        }, data);
    }

    populateKnownGroups = (groups) => {
        const actualGroups = (groups || []);
        this.allGroups = this.hashByValue(actualGroups);
        this.tapUsersGroups.setOptions(actualGroups.map(this.toSelectData).sort(this.sorter), true);
    }

    load = action(() => {
        if (!this.loadGroupsStatus.isLoading) {
            this.loadGroupsStatus.setLoading(this.apiGroupsList().then(
                (result) => this.populateKnownGroups(result?.groups || []),
                (e) => {
                    this.populateKnownGroups([]);
                    throw e;
                })
            );
        }
        return this.loadGroupsStatus.promise;
    });

    loadLastSyncGroup = () => {
        if (!this.loadLastSyncStatus.isLoading) {
            this.loadLastSyncStatus.setLoading(this.apiLoadLastSync().then((lastSync) => {
                if (!lastSync) {
                    this.lastSyncGroupDate = '';
                } else {
                    const date = moment.utc(lastSync);
                    const daysDiff = moment.utc().diff(date, 'days');
                    this.lastSyncGroupDate = daysDiff <= 30 ?
                        date.format('MMM/DD/YYYY [at] hh:mma [(GMT)]') : 'More than 30 Days ago';
                }
            }));
        }
        return this.loadLastSyncStatus.promise;
    }

    hashByValue = (arr) =>
        arr.reduce((acc, item) => {
            acc[this.toSelectValue(item)] = item;
            return acc;
        }, {});

    isGroupUnSynced = (gr) =>
        !this.allGroups[this.toSelectValue(gr)]

    toSelectData = (item) => {
        const unSynced = this.isGroupUnSynced(item);
        return {
            label: (item.name || item.id) + (unSynced ? ' (group not synchronized)' : ''),
            value: this.toSelectValue(item),
            hidden: unSynced,
            ...getGroupOptionDetails(item),
            ...item,
        };
    }

    sortHash = (gr) =>
        `${gr.hidden ? 'A|' : 'Z|'}${gr.icon ? 'A|' : 'Z|'}${gr.label}`;

    sorter = (a, b) =>
        (this.sortHash(a) > this.sortHash(b) ? 1 : -1);

    groupsSorterFactory = (options) => {
        const optsHash = this.hashByValue(options);
        return (v1, v2) => {
            const a = optsHash[v1];
            const b = optsHash[v2];
            return this.sorter(a, b);
        };
    }

    referencesUnSyncedGroups = () =>
        this.tapUsersGroups.getSelectedItems().some(this.isGroupUnSynced)

    toSelectValue = (item) => (typeof item.id !== 'undefined' ? item.id : (item.name || item));

    setValue = action((value) => {
        value = { ...DEFAULT_VALUES, ...value };
        this.originalValue = value;

        this.matchAllUserGroups.setValue(value.matchAllUserGroups);
        this.matchAllSenderEmail.setValue(value.matchAllSenderEmail);
        this.senderEmailAddresses.setValue(value.senderEmailAddresses);
        this.senderEmailDomains.setValue(value.senderEmailDomains);
        this.tapUsersGroups.setOptions(value.tapUsersGroups.map(this.toSelectData).sort(this.sorter));
        this.tapUsersGroups.setSelectedValues(value.tapUsersGroups.map(this.toSelectValue));
    });

    getValue = () => ({
        tapUsersGroups: this.tapUsersGroups.getSelectedItems(),
        matchAllUserGroups: this.matchAllUserGroups.getValue(),
        matchAllSenderEmail: this.matchAllSenderEmail.getValue(),
        senderEmailAddresses: this.senderEmailAddresses.getValue(),
        senderEmailDomains: this.senderEmailDomains.getValue(),
    });

    haveDifferentFlatItems = (arr1, arr2) =>
        arr1.length !== arr2.length ||
        arr1.some((item, index) =>
            this.toSelectValue(item) !== this.toSelectValue(arr2[index])
        );

    hasChanged = () => {
        const cur = this.getValue();
        const orig = this.originalValue;

        return orig.matchAllUserGroups !== cur.matchAllUserGroups ||
            orig.matchAllSenderEmail !== cur.matchAllSenderEmail ||
            this.haveDifferentFlatItems(orig.tapUsersGroups, cur.tapUsersGroups) ||
            this.haveDifferentFlatItems(orig.senderEmailAddresses, cur.senderEmailAddresses) ||
            this.haveDifferentFlatItems(orig.senderEmailDomains, cur.senderEmailDomains);
    }

    isValid = () => {
        const cur = this.getValue();

        return (cur.matchAllUserGroups || cur.tapUsersGroups.length > 0) &&
            (cur.matchAllSenderEmail || (
                (cur.senderEmailDomains.length > 0 || cur.senderEmailAddresses.length > 0) &&
                this.senderEmailAddresses.isValid() && this.senderEmailDomains.isValid()
            ));
    }
}
